import { StateService } from "@uirouter/angular";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from "@angular/core";
import { Subscription, take } from "rxjs";
import { IComment } from "@gtmhub/comments";
import { storage } from "@gtmhub/core/storage";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { GoalsChangedEventArgs, OKRsEventType } from "@gtmhub/okrs/events";
import { SizeOfGif } from "@gtmhub/shared/services/tenor-gif.service";
import { CurrentUserService, getCurrentUserId } from "@gtmhub/users";
import { BroadcastService } from "@webapp/core/broadcast/services/broadcast.service";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { ICustomField } from "@webapp/custom-fields/models/custom-fields.models";
import { filterCustomFieldsBySession } from "@webapp/custom-fields/utils/custom-fields-filter-util";
import { localize } from "@webapp/localization/utils/localization.utils";
import { UpdateMetricNoteComponent, UpdateMetricNoteModalData } from "@webapp/okrs/metrics/components/update-metric-note/update-metric-note.component";
import { IMetricSnapshot, Metric } from "@webapp/okrs/metrics/models/metric.models";
import { MetricsFacade } from "@webapp/okrs/metrics/services/metrics-facade.service";
import { ConfirmDeleteService } from "@webapp/shared/modal/confirm-delete.service";
import { cancelButton, primaryButton } from "@webapp/shared/modal/modal.utils";
import { UiModalService } from "@webapp/ui/modal/services/modal.service";

type SnapshotOrigin = "cascading_update" | "automatic_update" | "manual_update" | "notes_update" | "unsupported";

@Component({
  selector: "metric-snapshot-previews",
  templateUrl: "metric-snapshot-preview.component.html",
  styleUrls: ["metric-snapshot-preview.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DestroyService],
})
export class MetricSnapshotPreviewComponent implements OnInit, OnDestroy {
  @Input()
  public snapshot: IMetricSnapshot;

  @Input()
  public metric: Metric;

  @Input()
  public sourceMetric?: Metric;

  public isAdmin: boolean;
  public sizeOfGif = SizeOfGif;
  public currentUserId: string;
  public confidenceOverflow = "no-overflow";
  public snapshotAttainmentValue: number;
  public snapshotCurrentAttainmentValue: number;
  private eventSubscription: Subscription;

  constructor(
    private broadcastService: BroadcastService,
    private currentUserService: CurrentUserService,
    private modalService: UiModalService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private metricsFacade: MetricsFacade,
    private stateService: StateService,
    private confirmDeleteService: ConfirmDeleteService,
    private cdr: ChangeDetectorRef
  ) {}

  public get snapshotOrigin(): SnapshotOrigin {
    const isUserAction = !!this.snapshot.changedBy;
    const isCascadingUpdate = !!this.snapshot.sourceInfo;
    const isCascadingMetric = this.metric.cascadeType !== "";
    const isDynamicMetric = (this.metric.sourceMetricId && this.sourceMetric.insightName !== "") || (!this.metric.sourceMetricId && this.metric.insightName !== "");
    const hasComment = !!this.snapshot.comment;

    if (isCascadingUpdate) {
      return "cascading_update";
    } else if (!isUserAction) {
      return "automatic_update";
    } else if ((isCascadingMetric || isDynamicMetric) && hasComment && this.snapshot.valueChange === 0) {
      return "notes_update";
    }

    return "manual_update";
  }

  public ngOnInit(): void {
    this.snapshotAttainmentValue = this.formatSnapshotAttainmentValue(this.snapshot.attainmentChange, { toPercent: false });
    this.snapshotCurrentAttainmentValue = this.formatSnapshotAttainmentValue(this.snapshot.currentAttainment, { toPercent: true });
    this.isAdmin = this.currentUserService.userHasRole("admin");
    this.currentUserId = getCurrentUserId();

    this.eventSubscription = this.broadcastService.on<GoalsChangedEventArgs>(OKRsEventType.OKRS_GOALS_CHANGED).subscribe((args): void => {
      if (args.reason === "metricCommentEdit" && args.detail.metric?.id === this.metric.id) {
        const snapshotUpdate = (args.detail.metric.metricHistory || []).find((snapshot) => snapshot.id === this.snapshot.id);
        this.snapshot = Object.assign({}, this.snapshot, snapshotUpdate);
        this.cdr.detectChanges();
      }
    });
  }

  public ngOnDestroy(): void {
    this.eventSubscription.unsubscribe();
  }

  public confirmDeleteSnapshot(): void {
    const metricId = this.metric.id;
    const goalId = this.metric.goalId;
    const snapshotId = this.snapshot.id;

    this.confirmDeleteService.confirmDeleteItem({ itemKey: "key_result_update" }).subscribe(() => {
      this.metricsFacade
        .deleteSnapshot$(goalId, metricId, snapshotId)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.broadcastService.emit(OKRsEventType.OKRS_GOALS_CHANGED, { reason: "goalChanged" });
            this.broadcastService.emit("goalsChanged");
            this.broadcastService.emit("commentDeleted", { targetId: snapshotId } as IComment);
          },
          error: (err) => this.uiErrorHandlingService.handleModal(err),
        });
    });
  }

  public filterBySessionId = (customFields: ICustomField[]): ICustomField[] => {
    return filterCustomFieldsBySession(this.metric.sessionId, customFields);
  };

  public isCustomFieldDisabled = (): boolean => {
    return true;
  };

  public goToMetric(): void {
    this.stateService.go("^.^._metric.metric", { metricId: this.snapshot.sourceInfo.id });
  }

  public goToUpdateMetric(): void {
    this.stateService.go("._updateMetric.edit", { checkinId: this.snapshot.id });
  }

  public openUpdateMetricModal(): void {
    const updateMetricNoteModal$ = this.modalService.create<UpdateMetricNoteComponent, UpdateMetricNoteModalData>({
      uiContent: UpdateMetricNoteComponent,
      uiTitle: localize("edit_KR_update_note"),
      uiData: {
        metricId: this.metric.id,
        checkinId: this.snapshot.id,
      },
      uiFooter: [
        primaryButton(localize("update"), {
          disabled: (instance) => !!instance.indicators.saving || instance.loading || instance.updateMetricNoteForm.pristine,
          onClick: (instance) => {
            storage.remove("modalCloseReason");
            return instance.save();
          },
        }),
        cancelButton({
          onClick: () => {
            storage.remove("modalCloseReason");
          },
        }),
      ],
      uiWidth: 657,
    });

    updateMetricNoteModal$?.containerInstance.containerClick.pipe(take(1)).subscribe(() => {
      storage.set("modalCloseReason", "backdrop click");
    });

    updateMetricNoteModal$?.containerInstance.cancelTriggered.pipe(take(1)).subscribe(() => {
      storage.remove("modalCloseReason");
    });
  }

  private formatSnapshotAttainmentValue(progress: number, options: { toPercent: boolean }): number {
    if (progress > 0.99 && progress < 1) {
      // Double Bitwise NOT Operator oes the same as Math.floor but much faster
      return options.toPercent ? ~~(progress * 100) : ~~(progress * 100) / 100;
    }
    return options.toPercent ? +(progress * 100).toFixed(0) : +(progress * 100).toFixed(0) / 100;
  }
}
